// Copied/adapted from `pdt-old/utils.ts` (do not modify old files).

const SESSION_STORAGE_KEY = 'pdt_journey_data';

export interface BasicSearchContext {
  originCityCode: string;
  destinationCityCode: string;
  departureDate: string;
}

type JourneyData = {
  prevSearchContextId: string;
  journeyId: string;
};

let globalSearchContext: BasicSearchContext | undefined;

const getPDTJourneyIDFromSession = (): JourneyData => {
  try {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (data) return JSON.parse(data) as JourneyData;
  } catch (error) {
    // noop
  }
  return { prevSearchContextId: '', journeyId: '' };
};

const putPDTJourneyIDInSession = (data: JourneyData): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    // noop
  }
};

export const generateUUID = (): string => {
  try {
    // Prefer crypto if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (w?.crypto?.randomUUID) return w.crypto.randomUUID();
  } catch {
    // fallback below
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Get or create Journey ID based on search context.
 * Creates new journey ID when search context changes (from-to-date).
 */
export const getJourneyId = (
  searchContext?: BasicSearchContext,
): string | null => {
  try {
    const searchContextInScope = searchContext || globalSearchContext;
    if (!searchContextInScope) return null;

    globalSearchContext = searchContextInScope;

    const { originCityCode, destinationCityCode, departureDate } =
      searchContextInScope;
    const newSearchContextId = `${originCityCode}-${destinationCityCode}-${departureDate}`;

    const { prevSearchContextId, journeyId } = getPDTJourneyIDFromSession();
    let newJourneyId = journeyId;

    if (newSearchContextId !== prevSearchContextId) {
      newJourneyId = generateUUID();
      putPDTJourneyIDInSession({
        prevSearchContextId: newSearchContextId,
        journeyId: newJourneyId,
      });
    }

    return newJourneyId || null;
  } catch (error) {
    // noop
    return null;
  }
};

/**
 * Calculate advance purchase days: days from today to departure date.
 */
export const calculateAdvancePurchase = (
  departureDate?: Date,
): string | null => {
  try {
    if (!departureDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departure = new Date(departureDate);
    departure.setHours(0, 0, 0, 0);

    const diffTime = departure.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays.toString();
  } catch (error) {
    // noop
    return null;
  }
};
